import { TrainStopProgressStep } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportSectionsAiGeneratedStatusChoices,
  ApiReportTemplateExportFrameworkChoices,
} from "@api/graphql/types-and-hooks";
import { EvaluationSubRoute, orderedEvaluationSubRoutes } from "@pages/EvaluationDetails/EvaluationDetails";
import { ReportNode } from "@providers/ReportProvider";

export const evaluationTrainTopStatusFromCurrentRoute = (
  currentRoute: EvaluationSubRoute,
  navStepRoute: EvaluationSubRoute,
  evaluation: ReportNode,
  existingAssessmentsFound: boolean,
): TrainStopProgressStep["status"] => {
  const indexOfCurrentRoute = orderedEvaluationSubRoutes.indexOf(currentRoute);
  const indexOfNavStepRoute = orderedEvaluationSubRoutes.indexOf(navStepRoute);

  // If the route matches the current, always mark status as current
  if (currentRoute === navStepRoute) return "current";
  // if the route is before our current one, it must be complete since it's before
  if (indexOfNavStepRoute < indexOfCurrentRoute) return "complete";

  switch (navStepRoute) {
    case "upload-files":
      if (existingAssessmentsFound) {
        return "complete";
      }
      if (evaluation.reportClientDetails?.clientFirstName) {
        return "reached";
      }
      return "upcoming";

    case "report-part-1":
    case "report-part-2":
    case "conclusions":
      if (
        evaluation.reportSections.edges
          .map((edge) => edge?.node)
          .filter((section) => section?.pageLocation === navStepRoute)
          .every(
            (section) =>
              section?.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.Pending ||
              section?.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.ManualEntry,
          )
      ) {
        return "upcoming";
      }
      if (
        evaluation.reportSections.edges
          .map((edge) => edge?.node)
          .filter((section) => section?.pageLocation === navStepRoute)
          .some(
            (section) =>
              section?.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending,
          )
      ) {
        return "reached";
      }
      return "complete";
    case "recommendations":
      if (
        evaluation.reportSections.edges
          .map((edge) => edge?.node)
          .filter((section) => section?.pageLocation === "recommendations").length > 0
      ) {
        return "complete";
      }
      if (
        evaluation.reportSections.edges
          .map((edge) => edge?.node)
          .filter((section) => section?.pageLocation === "conclusions").length === 0
      ) {
        return "upcoming";
      }
      if (
        evaluation.reportSections.edges
          .map((edge) => edge?.node)
          .filter((section) => section?.pageLocation === "recommendations").length === 0
      ) {
        return "reached";
      }

      return "complete";
    case "client-details":
      return "complete";
    case "appendix":
      return "reached";
    case "review-and-sign":
      if (
        evaluation.reportSections.edges
          .map((edge) => edge?.node)
          .filter((section) => section?.pageLocation === "recommendations").length > 0
      ) {
        return "reached";
      }
      return "upcoming";
  }
};

// A template's `unifiedExport` field routes docxtemplater orgs through the handlebars server-side (Temporal/S3) export pipeline.
export const shouldUseHandlebarsExportPath = (
  exportFramework: ApiReportTemplateExportFrameworkChoices | null | undefined,
  unifiedExport: boolean | undefined,
): boolean => {
  const isDocxTemplaterExport = exportFramework === ApiReportTemplateExportFrameworkChoices.Docxtemplater;
  const isHandlebarsExport = exportFramework === ApiReportTemplateExportFrameworkChoices.Handlebars;
  return isHandlebarsExport || (isDocxTemplaterExport && !!unifiedExport);
};
